/**
 * OperationTitleDebtsPage — Tela de duplicatas/parcelas de uma operação específica.
 * Acesso: /settlement/operations/:contractOperationId
 * Wireframe: settlement/docs/po/wireframe-fluxo-baixa-e-liquidacao.pdf
 *
 * Colunas (PO): Identificador | Valor vencimento | Data vencimento | Status | Valor recebido | Tipo | Conta | Meio | Ações
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaFileInvoiceDollar } from 'react-icons/fa';
import BadgeStatus from '../../atoms/BadgeStatus';
import { statusColors, statusLabels, settlementTypeLabels } from './constants/settlement';
import { listTitleDebts, getTitleDebtMovements } from './services/settlementApi';
import PaymentModal from './modals/PaymentModal';
import BatchPaymentModal from './modals/BatchPaymentModal';
import MovementsModal from './modals/MovementsModal';
import ReversalModal from './modals/ReversalModal';

const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value || '0') : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (date: string | null) => {
    if (!date) return '-';
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
};

const OperationTitleDebtsPage = () => {
    const { contractOperationId } = useParams<{ contractOperationId: string }>();
    const navigate = useNavigate();

    const [titleDebts, setTitleDebts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedDebt, setSelectedDebt] = useState<any>(null);
    const [selectedForPayment, setSelectedForPayment] = useState<any>(null);
    const [selectedForReversal, setSelectedForReversal] = useState<any>(null);
    const [selectedForBatch, setSelectedForBatch] = useState<any[]>([]);
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchDebts = useCallback(async () => {
        if (!contractOperationId) return;
        setLoading(true);
        try {
            const data = await listTitleDebts({
                contract_operation_id: contractOperationId,
                page: '1',
                size: '200',
                sort: 'due_date',
            });
            setTitleDebts(data.list || []);
        } catch (e) {
            console.error('Failed to fetch title debts', e);
        }
        setLoading(false);
    }, [contractOperationId, refreshKey]);

    useEffect(() => { fetchDebts(); }, [fetchDebts]);

    const handleRefresh = () => setRefreshKey(k => k + 1);

    const toggleBatchSelection = (debt: any) => {
        setSelectedForBatch(prev =>
            prev.find(d => d.id === debt.id)
                ? prev.filter(d => d.id !== debt.id)
                : [...prev, debt]
        );
    };

    const openDebts = titleDebts.filter(td => td.status === 'OPEN' || td.status === 'PARTIALLY_PAID');
    const totalAmount = titleDebts.reduce((s, td) => s + parseFloat(td.amount || '0'), 0);
    const totalPaid = titleDebts.reduce((s, td) => s + parseFloat(td.paid_amount || '0'), 0);
    const contractNumber = titleDebts[0]?.contract_number || '-';

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
                    <FaArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                        Operação {contractNumber}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {contractOperationId?.slice(0, 8)}... • {titleDebts.length} título(s)
                    </p>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Valor Total</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(totalAmount)}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Pago</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(totalPaid)}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Saldo Devedor</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(totalAmount - totalPaid)}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Em Aberto</p>
                    <p className="text-lg font-bold text-yellow-600">{openDebts.length} parcela(s)</p>
                </div>
            </div>

            {/* Actions bar */}
            <div className="flex justify-between items-center mb-4">
                <div className="flex gap-2">
                    {selectedForBatch.length > 0 && (
                        <button
                            onClick={() => setShowBatchModal(true)}
                            className="px-3 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-2"
                        >
                            <FaFileInvoiceDollar /> Baixa em Lote ({selectedForBatch.length})
                        </button>
                    )}
                </div>
                <button onClick={handleRefresh} className="px-3 py-2 text-sm border rounded hover:bg-gray-50 dark:hover:bg-slate-700">
                    Atualizar
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <p className="text-center text-gray-500 py-8">Carregando...</p>
            ) : (
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="py-3 px-3 w-8">
                                    <input type="checkbox" className="rounded"
                                        onChange={e => setSelectedForBatch(e.target.checked ? openDebts : [])}
                                        checked={selectedForBatch.length === openDebts.length && openDebts.length > 0}
                                    />
                                </th>
                                <th className="py-3 px-3">Identificador</th>
                                <th className="py-3 px-3 text-right">Valor Vencimento</th>
                                <th className="py-3 px-3">Data Vencimento</th>
                                <th className="py-3 px-3">Status</th>
                                <th className="py-3 px-3 text-right">Valor Recebido</th>
                                <th className="py-3 px-3">Tipo</th>
                                <th className="py-3 px-3">Conta</th>
                                <th className="py-3 px-3">Meio</th>
                                <th className="py-3 px-3">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {titleDebts.map((td, idx) => {
                                const isOpen = td.status === 'OPEN' || td.status === 'PARTIALLY_PAID';
                                const isSelected = selectedForBatch.some(d => d.id === td.id);
                                return (
                                    <tr key={td.id} className={`${idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-slate-700`}>
                                        <td className="py-2 px-3">
                                            {isOpen && (
                                                <input type="checkbox" className="rounded"
                                                    checked={isSelected}
                                                    onChange={() => toggleBatchSelection(td)}
                                                />
                                            )}
                                        </td>
                                        <td className="py-2 px-3 font-medium cursor-pointer hover:text-blue-600"
                                            onClick={() => setSelectedDebt(td)}>
                                            {td.document_title || `Parcela ${td.installment_number || '-'}`}
                                            {td.amendment_number > 0 && (
                                                <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1 rounded dark:bg-blue-900 dark:text-blue-300">
                                                    Adit #{td.amendment_number}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 text-right">{formatCurrency(td.amount)}</td>
                                        <td className="py-2 px-3">
                                            {formatDate(td.due_date)}
                                            {td.days_overdue > 0 && (
                                                <span className="ml-1 text-xs text-red-600 font-medium">({td.days_overdue}d)</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3">
                                            <BadgeStatus text={statusLabels[td.status] || td.status} color={statusColors[td.status] || 'gray'} />
                                        </td>
                                        <td className="py-2 px-3 text-right">{parseFloat(td.paid_amount || '0') > 0 ? formatCurrency(td.paid_amount) : '-'}</td>
                                        <td className="py-2 px-3 text-xs">{/* TODO: último movement settlement_type */}-</td>
                                        <td className="py-2 px-3 text-xs">{/* TODO: último invoice bank_account */}-</td>
                                        <td className="py-2 px-3 text-xs">{/* TODO: último invoice payment_method */}-</td>
                                        <td className="py-2 px-3">
                                            <div className="flex gap-1">
                                                {isOpen && (
                                                    <button
                                                        onClick={() => setSelectedForPayment(td)}
                                                        className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                                    >Baixar</button>
                                                )}
                                                {(td.status === 'PAID' || td.status === 'PARTIALLY_PAID') && (
                                                    <button
                                                        onClick={() => setSelectedForReversal(td)}
                                                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                                    >Reverter</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modals */}
            {selectedDebt && (
                <MovementsModal
                    titleDebt={selectedDebt}
                    onClose={() => setSelectedDebt(null)}
                />
            )}

            {selectedForPayment && (
                <PaymentModal
                    titleDebt={selectedForPayment}
                    onClose={() => setSelectedForPayment(null)}
                    onSuccess={handleRefresh}
                />
            )}

            {showBatchModal && (
                <BatchPaymentModal
                    titleDebts={selectedForBatch}
                    onClose={() => { setShowBatchModal(false); setSelectedForBatch([]); }}
                    onSuccess={handleRefresh}
                />
            )}

            {selectedForReversal && (
                <ReversalModal
                    titleDebt={selectedForReversal}
                    onClose={() => setSelectedForReversal(null)}
                    onSuccess={handleRefresh}
                />
            )}
        </div>
    );
};

export default OperationTitleDebtsPage;
